import { Alert, Button } from "reactstrap";
import {
  PaymentRequestV1,
  ProviderService,
  SaveTestersConsentService,
  UserService,
} from "../generated";
import React, { Component } from "react";
import { Redirect, RouteComponentProps } from "react-router";

import { Policy } from "./policy";
import DOMPurify from "dompurify";

export interface State {
  loading: boolean;
  success: boolean;
  url: string;
  name: string;
  provider: string;
  request: PaymentRequestV1;
  requests: PaymentRequestV1[];
  confetti: boolean;
  status: string;
  instruction: string;
  paymentMethod: string;
  isInvited: boolean;
  isAuthenticated: boolean;
  area: string;
}

export class PaymentsGetStarted extends Component<RouteComponentProps, State> {
  constructor(props: RouteComponentProps) {
    super(props);

    this.state = {
      loading: true,
      success: false,
      url: null,
      name: null,
      provider: null,
      request: null,
      requests: null,
      confetti: false,
      status: "Full",
      instruction: null,
      paymentMethod: null,
      isInvited: false,
      isAuthenticated: false,
      area: null,
    };
  }

  async componentDidMount() {
    const isAuthenticated = await UserService.userIsAuthenticated();
    if (isAuthenticated) {
      const isInvited = await UserService.userIsInvited();
      this.setState({
        isInvited,
        isAuthenticated,
      });
    }

    const rawCode = new URLSearchParams(this.props.location.search).get("code");
    const sanitizedCode = DOMPurify.sanitize(rawCode);

    const data = await ProviderService.providerPayment({
      code: sanitizedCode,
    });

    if (data.paymentMethod === "BulkPayments") {
      const authResult = await ProviderService.providerAuthenticate({
        code: sanitizedCode,
      });

      data.url = authResult.url;
    }
    this.setState({
      loading: false,
      success: data.success,
      url: data.url,
      name: data.name,
      provider: data.provider || "your bank",
      status: data.status,
      request: data.request,
      requests: data.requests,
      confetti: data.confetti,
      instruction: data.instruction,
      paymentMethod: data.paymentMethod,
      area: data.area,
    });
  }

  renderInstruction() {
    if (!this.state.instruction) return;

    return (
      <Alert color="primary">
        <strong>Instruction:</strong> {this.state.instruction}
      </Alert>
    );
  }

  warnIfNotFull() {
    if (this.state.status === "Full") return;

    if (this.state.status === "Limited")
      return (
        <Alert color="warning">
          It would seem that {this.state.provider} might be currently
          experiencing some issues.
        </Alert>
      );

    if (this.state.status === "Disabled")
      return (
        <Alert color="danger">
          It would seem that {this.state.provider} is experiencing some severe
          issues at the moment, and is not available for testing.
        </Alert>
      );
  }

  renderConfettiWelcome() {
    return (
      <div>
        <h2>
          Hi {this.state.name || "there!"}{" "}
          <span role="img" aria-label="*wave*">
            👋
          </span>
        </h2>
        <br />
        <p>
          Thank you for helping us testing the connection to{" "}
          {this.state.provider}.
        </p>

        {this.renderPaymentDetails()}

        <p>
          Clicking the button below will make the payment using{" "}
          {this.state.provider}.
        </p>

        <p>{this.renderPrivacyNotice()}</p>

        {this.warnIfNotFull()}

        {this.renderInstruction()}

        {this.state.status !== "Disabled" && (
          <Button
            onClick={() => (window.location.href = this.state.url)}
            color="primary"
          >
            Let&apos;s go!
          </Button>
        )}
      </div>
    );
  }

  renderBoringWelcome() {
    return (
      <div>
        {this.state.name && (
          <h2>
            Hi {this.state.name}
            <br />
          </h2>
        )}
        <p>
          Thank you for helping us testing the connection to{" "}
          {this.state.provider}.
        </p>

        {this.renderPaymentDetails()}

        <p>Please click the button below to make the payment.</p>

        <p>{this.renderPrivacyNotice()}</p>

        {this.warnIfNotFull()}

        {this.renderInstruction()}

        {this.state.status !== "Disabled" && (
          <Button onClick={() => this.makePayment()} color="primary">
            Make Payment
          </Button>
        )}
      </div>
    );
  }

  async makePayment() {
    const rawCode = new URLSearchParams(this.props.location.search).get("code");
    const sanitizedCode = DOMPurify.sanitize(rawCode);

    await SaveTestersConsentService.SaveTestersConsent({
      missionId: sanitizedCode,
    });
    window.location.href = this.state.url;
  }

  renderPrivacyNotice() {
    return (
      <Policy
        area={this.state.area}
        providerId={this.state.provider}
        buttonPosition="bellow"
      />
    );
  }

  renderPaymentDetails() {
    let requests =
      this.state.paymentMethod === "BulkPayments"
        ? this.state.requests
        : [this.state.request];

    return (
      <Alert color="primary">
        <h4 className="alert-heading">Payment details</h4>

        <p>This is the payment that has been requested</p>

        <hr />

        {requests?.map((req, index) => {
          return (
            <dl className="row mb-0" key={index}>
              <dt className="col-sm-3">Amount</dt>
              <dd className="col-sm-9">
                {req.amount.value.toLocaleString(navigator.language, {
                  minimumFractionDigits: 2,
                })}{" "}
                {req.amount.currency}
              </dd>

              <dt className="col-sm-3">Destination</dt>
              <dd className="col-sm-9">
                {req.destination.bban && (
                  <span>
                    {req.destination.bban.bankCode}-
                    {req.destination.bban.accountNumber}
                  </span>
                )}
                {req.destination.iban && (
                  <span>{req.destination.iban.ibanNumber}</span>
                )}
              </dd>

              {req.message && [
                <dt className="col-sm-3">Message</dt>,
                <dd className="col-sm-9">
                  <span>{req.message}</span>
                </dd>,
              ]}

              {req.transactionText && [
                <dt className="col-sm-3">Statement Text</dt>,
                <dd className="col-sm-9">
                  <span>{req.transactionText}</span>
                </dd>,
              ]}
              <br />
              <hr />
            </dl>
          );
        })}
      </Alert>
    );
  }

  render() {
    const { isAuthenticated, isInvited, loading, success, confetti } =
      this.state;

    if (loading) {
      return <p>Loading, please wait...</p>;
    }

    if (isAuthenticated && isInvited) {
      return <Redirect to={`/onboarding`} />;
    }

    if (success && confetti) {
      return this.renderConfettiWelcome();
    }

    if (success) {
      return this.renderBoringWelcome();
    }

    return (
      <div>
        <h2>Error</h2>
        <br />
        <p>Something went wrong.</p>
      </div>
    );
  }
}
